import {
  ArrowClockwise,
  Info,
  ListBullets,
  PencilSimple,
  Plus,
  WarningCircle,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import { useGetLists } from '../hooks/use-lists';
import type { CampaignList } from '../types/lists.types';
import {
  formatListCreatedDate,
  formatListsSubtitle,
  formatMemberCountLabel,
} from '../utils/list-api-mapper';
import { ListFormModal } from './list-form-modal';

const tableHeadClassName =
  'sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 py-3 text-left text-[13px] font-semibold leading-[18px] text-neutral-600';

export const ListsOverview = () => {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useGetLists();
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingList, setEditingList] = useState<CampaignList | undefined>(undefined);

  const lists = useMemo(() => data?.items ?? [], [data?.items]);
  const isError = Boolean(error);
  const showLoading = isLoading && lists.length === 0;
  const totalMemberships = useMemo(
    () => lists.reduce((sum, list) => sum + list.memberCount, 0),
    [lists]
  );

  const handleCreateClick = () => {
    setEditingList(undefined);
    setFormModalOpen(true);
  };

  const handleEditClick = (list: CampaignList) => {
    setEditingList(list);
    setFormModalOpen(true);
  };

  const subtitle = showLoading
    ? 'Loading…'
    : isError
      ? 'Could not load lists.'
      : lists.length === 0
        ? '0 lists'
        : formatListsSubtitle(lists.length, totalMemberships);

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-5">
        <div className="flex items-start gap-2.5 rounded-lg bg-primary-50 px-3.5 py-3">
          <Info className="mt-0.5 size-4 shrink-0 text-primary-600" aria-hidden />
          <p className="text-[13px] leading-[18px] text-primary-900">
            This page covers <strong>static lists</strong> — fixed, marketer-curated audiences.
            Dynamic, rule-based segments live on the{' '}
            <Link to="/segments" className="font-medium underline">
              Segments
            </Link>{' '}
            page.
          </p>
        </div>

        <div className="flex flex-wrap items-start gap-4">
          <div>
            <h1 className="text-[28px] font-semibold leading-9 text-neutral-900">Lists</h1>
            <p className="mt-0.5 text-sm leading-5 text-neutral-600">{subtitle}</p>
          </div>
          <div className="flex-1" />
          <Button
            type="button"
            className="h-10 bg-primary-600 px-4 text-white shadow-e1 hover:bg-primary-700"
            onClick={handleCreateClick}
          >
            <Plus className="size-4" aria-hidden />
            New list
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-e1">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full min-w-[760px] border-collapse text-sm leading-5">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50">
                  <th className={tableHeadClassName}>Name</th>
                  <th className={tableHeadClassName}>Description</th>
                  <th className={tableHeadClassName}>Members</th>
                  <th className={tableHeadClassName}>Created</th>
                  <th className={cn(tableHeadClassName, 'w-14')} />
                </tr>
              </thead>
              <tbody>
                {showLoading
                  ? Array.from({ length: 3 }).map((_, index) => (
                      <tr key={`skeleton-${index}`} className="border-b border-neutral-100">
                        <td className="px-4 py-3">
                          <span className="block h-4 w-32 animate-pulse rounded bg-neutral-200" />
                        </td>
                        <td className="px-4 py-3">
                          <span className="block h-4 w-48 animate-pulse rounded bg-neutral-200" />
                        </td>
                        <td className="px-4 py-3">
                          <span className="block h-4 w-20 animate-pulse rounded bg-neutral-200" />
                        </td>
                        <td className="px-4 py-3">
                          <span className="block h-4 w-24 animate-pulse rounded bg-neutral-200" />
                        </td>
                        <td className="px-4 py-3" />
                      </tr>
                    ))
                  : null}

                {!showLoading && isError ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center">
                      <div className="mx-auto flex max-w-[360px] flex-col items-center gap-2">
                        <WarningCircle className="size-7 text-error" aria-hidden />
                        <p className="font-medium text-neutral-900">Could not load lists</p>
                        <p className="text-[13px] leading-[18px] text-neutral-500">
                          Something went wrong on our end. Try again.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-1 h-8 border-neutral-200"
                          onClick={() => void refetch()}
                        >
                          <ArrowClockwise className="size-4" aria-hidden />
                          Retry
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {!showLoading && !isError && lists.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center">
                      <div className="mx-auto flex max-w-[360px] flex-col items-center gap-2">
                        <ListBullets className="size-7 text-neutral-400" aria-hidden />
                        <p className="font-medium text-neutral-900">No lists yet</p>
                        <p className="text-[13px] leading-[18px] text-neutral-500">
                          Create a list to start grouping contacts for targeting.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {!showLoading &&
                  !isError &&
                  lists.map((list) => (
                    <tr
                      key={list.id}
                      className="data-row cursor-pointer border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50"
                      onClick={() => navigate(`/lists/${encodeURIComponent(list.id)}`)}
                    >
                      <td className="px-4 py-3 align-middle font-medium text-neutral-900">
                        {list.name}
                      </td>
                      <td className="px-4 py-3 align-middle text-neutral-600">
                        {list.description ? (
                          list.description
                        ) : (
                          <span className="text-neutral-400">No description</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="inline-flex items-center rounded-full bg-neutral-100 px-2.5 py-0.5 text-[12px] font-medium leading-4 text-neutral-800">
                          {formatMemberCountLabel(list.memberCount)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-middle text-neutral-600">
                        {formatListCreatedDate(list.createdAt)}
                      </td>
                      <td
                        className="px-4 py-3 align-middle text-right"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="inline-flex size-8 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800"
                          title="Edit list"
                          aria-label={`Edit ${list.name}`}
                          onClick={() => handleEditClick(list)}
                        >
                          <PencilSimple className="size-4" aria-hidden />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ListFormModal
        open={formModalOpen}
        onOpenChange={(open) => {
          setFormModalOpen(open);
          if (!open) setEditingList(undefined);
        }}
        editingList={editingList}
      />
    </>
  );
};
